import { useCallback, useEffect, useState } from "react";
import { Users, Wrench, IndianRupee, Percent, CheckCircle2, Eye } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { QuickAssign } from "@/components/QuickAssign";
import { TaskVerifyDialog } from "@/components/TaskVerifyDialog";
import { CategoryBadge } from "@/components/Badges";
import { PayrollApprovalPanel } from "@/pages/admin/PayrollApprovalPanel";
import { useAuth } from "@/context/AuthContext";
import { api, inr, STAGE, fmtDateTime } from "@/lib/api";

const Metric = ({ icon: Icon, label, value, sub, testid, i }) => (
  <div className="fade-up bg-white rounded-2xl border p-5" style={{ animationDelay: `${i * 70}ms` }} data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">{label}</div>
      <Icon className="h-4 w-4 text-sky-600" />
    </div>
    <div className="font-heading text-3xl font-extrabold text-slate-900 mt-3 tracking-tight">{value}</div>
    <div className="text-xs text-slate-500 mt-1">{sub}</div>
  </div>
);

const Panel = ({ title, children, className = "", testid }) => (
  <section className={`bg-white rounded-2xl border p-5 ${className}`} data-testid={testid}>
    <h2 className="font-heading text-lg font-bold text-slate-900 mb-4">{title}</h2>
    {children}
  </section>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [view, setView] = useState(null);
  const load = useCallback(() => api.get("/dashboard").then((r) => setD(r.data)), []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  if (!d) return <div className="grid sm:grid-cols-4 gap-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-32 rounded-2xl bg-slate-200 animate-pulse" />)}</div>;
  const p = d.pipeline;
  const chart = p.by_stage.map((s) => ({ name: STAGE[s.stage], value: s.value, count: s.count }));

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{user.role === "director" ? "Executive Window" : "HR Dashboard"}</div>
        <h1 className="font-heading text-4xl font-extrabold text-slate-900 tracking-tight mt-1">Operations Overview</h1>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Metric i={0} icon={Users} label="Today's Attendance" value={`${d.attendance.on_duty} / ${d.attendance.total}`} sub="Staff on duty" testid="metric-attendance" />
        <Metric i={1} icon={Wrench} label="Service / MGPS Tickets" value={`${d.tickets.open} Active`} sub={`${d.tickets.completed_month} completed this month`} testid="metric-tickets" />
        <Metric i={2} icon={IndianRupee} label="Open Pipeline Value" value={inr(p.open_value)} sub={`${inr(p.won_value)} won`} testid="metric-pipeline" />
        <Metric i={3} icon={Percent} label="Lead Conversion" value={`${p.conversion_rate}%`} sub={`${p.won} won · ${p.lost} lost · ${p.total_leads} total`} testid="metric-conversion" />
      </div>
      <div className="grid xl:grid-cols-3 gap-6">
        <Panel title="Quick Task Assignment" testid="quick-assign-panel"><QuickAssign onCreated={load} /></Panel>
        <Panel title="Pipeline by Stage" className="xl:col-span-2" testid="pipeline-chart-panel">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
              <BarChart data={chart}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} tick={{ fontSize: 11 }} width={50} />
                <Tooltip formatter={(v) => inr(v)} />
                <Bar dataKey="value" fill="#0284C7" radius={[6, 6, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 grid sm:grid-cols-3 gap-2" data-testid="pipeline-by-city">
            {p.by_city.map((c) => (
              <div key={c.city} className="rounded-lg border px-3 py-2 text-sm flex justify-between">
                <span className="text-slate-700">{c.city} <span className="text-slate-400">({c.count})</span></span>
                <span className="font-mono-num font-semibold">{inr(c.value)}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
      <div className="grid xl:grid-cols-2 gap-6">
        <Panel title="Recently Completed — Verify" testid="recent-completed-panel">
          <div className="divide-y">
            {d.recent_completed.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No completed tasks yet.</div>}
            {d.recent_completed.map((t) => (
              <div key={t.id} className="py-3 flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-slate-900 truncate">{t.task_title}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2"><CategoryBadge category={t.category} />{t.client?.hospital_name} · {t.assignee_name} · {fmtDateTime(t.completion_time)}</div>
                </div>
                <button onClick={() => setView(t)} className="h-9 px-3 rounded-lg border text-sm font-medium hover:bg-slate-50 flex items-center gap-1" data-testid={`verify-task-button-${t.id}`}><Eye className="h-4 w-4" />Review</button>
              </div>
            ))}
          </div>
        </Panel>
        {user.role === "director" && <PayrollApprovalPanel />}
      </div>
      <TaskVerifyDialog task={view} onOpenChange={() => setView(null)} />
    </div>
  );
}
